<template>
  <page-main title="Export Resumation Profile" noBottom :error="error">
    <template v-slot:aside>
      Download your complete career profile data in your preferred format.
    </template>
    <template v-slot:nav>
      <v-card class="pa-4 mb-3" color="primary" dark>
        <v-icon size="28" class="mb-2">mdi-database-export</v-icon>
        <div class="text-h6 mb-1">Career Profile</div>
        <div class="text-body-2 opacity-80">
          {{ profileStats }}
        </div>
      </v-card>

      <v-card class="pa-3 mb-3" outlined>
        <div class="text-subtitle-2 font-weight-medium mb-3">
          Export Options
        </div>
        <v-btn
          color="primary"
          block
          class="mb-2"
          :loading="exportingDocx"
          @click="exportPdf"
        >
          <v-icon left>mdi-file-pdf-box</v-icon>
          Download PDF
        </v-btn>
        <div class="text-caption text--secondary text-center mt-1">
          Resumation Profile (PDF)
        </div>
      </v-card>

      <v-card class="pa-3" outlined>
        <div class="text-subtitle-2 font-weight-medium mb-2">
          What's Included
        </div>
        <div class="text-caption text--secondary">
          <div
            v-for="section in includedSections"
            :key="section.label"
            class="d-flex align-center mb-1"
          >
            <v-icon
              x-small
              :color="section.hasData ? 'success' : 'grey lighten-1'"
              class="mr-2"
            >
              {{ section.hasData ? "mdi-check-circle" : "mdi-circle-outline" }}
            </v-icon>
            <span :class="{ 'text--disabled': !section.hasData }">{{
              section.label
            }}</span>
          </div>
        </div>
      </v-card>
    </template>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <div class="export-document-frame">
            <div id="export-preview" class="export-rich-preview">
              <div class="export-header mb-6">
                <h2 class="export-title">
                  {{ contactName || "Career Profile" }}
                </h2>
                <div
                  v-if="contactTitle"
                  class="text-subtitle-1 text--secondary"
                >
                  {{ contactTitle }}
                </div>
                <div
                  v-if="languagesDisplay"
                  class="text-caption text--secondary mt-1"
                >
                  Languages: {{ languagesDisplay }}
                </div>
                <div class="text-caption text--secondary mt-1">
                  Exported {{ exportDate }}
                </div>
              </div>

              <v-divider class="mb-5"></v-divider>

              <!-- Contact Information -->
              <div v-if="hasContactData" class="export-section mb-5">
                <div class="export-section-header">
                  <v-icon small color="primary" class="mr-2"
                    >mdi-account-outline</v-icon
                  >
                  <span class="text-subtitle-1 font-weight-bold primary--text"
                    >Contact Information</span
                  >
                </div>
                <div class="export-section-body">
                  <div class="export-contact-grid">
                    <div v-if="email" class="export-contact-item">
                      <v-icon x-small class="mr-1">mdi-email-outline</v-icon>
                      {{ email }}
                    </div>
                    <div v-if="phoneNumber" class="export-contact-item">
                      <v-icon x-small class="mr-1">mdi-phone-outline</v-icon>
                      {{ phoneNumber }}
                    </div>
                    <div v-if="linkedin" class="export-contact-item">
                      <v-icon x-small class="mr-1">mdi-linkedin</v-icon>
                      {{ linkedin }}
                    </div>
                    <div v-if="otherLink" class="export-contact-item">
                      <v-icon x-small class="mr-1">mdi-web</v-icon>
                      {{ otherLink }}
                    </div>
                    <div v-if="contactLocation" class="export-contact-item">
                      <v-icon x-small class="mr-1"
                        >mdi-map-marker-outline</v-icon
                      >
                      {{ contactLocation }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Professional Summary -->
              <div v-if="professionalSummary" class="export-section mb-5">
                <div class="export-section-header">
                  <v-icon small color="primary" class="mr-2"
                    >mdi-text-box-outline</v-icon
                  >
                  <span class="text-subtitle-1 font-weight-bold primary--text"
                    >Professional Summary</span
                  >
                </div>
                <div class="export-section-body">
                  <p class="text-body-1 mb-0" style="line-height: 1.7;">
                    {{ professionalSummary }}
                  </p>
                </div>
              </div>

              <!-- Superpowers (generated on the Career Profile, included here
                   when present). Each is AI interpretation of the person's own
                   evidence; the cited achievements are the facts. A decision-
                   authority disclaimer follows the list. -->
              <div
                v-if="cachedSuperpowers.length > 0"
                class="export-section mb-5"
              >
                <div class="export-section-header">
                  <v-icon small color="primary" class="mr-2"
                    >mdi-flash-outline</v-icon
                  >
                  <span class="text-subtitle-1 font-weight-bold primary--text"
                    >Superpowers</span
                  >
                  <span class="text-caption text--secondary ml-2"
                    >({{ cachedSuperpowers.length }})</span
                  >
                </div>
                <div class="export-section-body">
                  <div
                    v-for="(sp, spIdx) in cachedSuperpowers"
                    :key="'exp-sp-' + spIdx"
                    class="mb-3"
                    :class="{ 'pt-3 export-work-divider': spIdx > 0 }"
                  >
                    <div class="text-body-1 font-weight-medium">
                      {{ spIdx + 1 }}. {{ sp.name }}
                    </div>
                    <div
                      v-if="sp.oneLiner"
                      class="text-body-2 text--secondary mb-1"
                    >
                      {{ sp.oneLiner }}
                    </div>
                    <div
                      v-if="sp.evidence && sp.evidence.length"
                      class="export-star-field"
                    >
                      <span class="export-star-label">Backed by:</span>
                      <div
                        v-for="(ev, evIdx) in sp.evidence"
                        :key="'exp-sp-' + spIdx + '-ev-' + evIdx"
                        class="ml-3"
                      >
                        &bull; {{ ev }}
                      </div>
                    </div>
                    <div
                      v-if="sp.roles && sp.roles.length"
                      class="text-caption text--secondary mt-1"
                    >
                      Where it showed up: {{ sp.roles.join(", ") }}
                    </div>
                  </div>
                  <div class="export-superpowers-disclaimer mt-2">
                    Superpowers are an AI interpretation of this person's own
                    profile evidence - the cited achievements and wins are the
                    facts. They do not make any hiring decision; use them as a
                    starting point and verify against the underlying evidence.
                  </div>
                </div>
              </div>

              <!-- PR-FIT-NOTES-FIX: Personal Statement section removed.
                   The field it was pulling from (fitAdditionalNotes) is
                   actually the "additional context" textarea at the end
                   of the Fit & Preferences questionnaire — labeling it
                   "Personal Statement" misrepresented the data. The note
                   now appears as an Additional Context block inside the
                   Workplace Fit Preferences section below, where it
                   belongs. -->

              <!-- Identity & Career Positioning -->
              <div v-if="hasIdentityData" class="export-section mb-5">
                <div class="export-section-header">
                  <v-icon small color="primary" class="mr-2">mdi-target</v-icon>
                  <span class="text-subtitle-1 font-weight-bold primary--text"
                    >Identity & Career Positioning</span
                  >
                </div>
                <div class="export-section-body">
                  <div v-if="adjectiveList.length > 0" class="text-body-2 mb-2">
                    <strong>Professional Descriptors:</strong>
                    {{ adjectiveList.join(", ") }}
                  </div>
                  <div v-if="roleList.length > 0" class="text-body-2 mb-2">
                    <strong>Professional Archetypes:</strong>
                    {{ roleList.join(", ") }}
                  </div>
                  <div
                    v-if="careerInterestList.length > 0"
                    class="text-body-2 mb-2"
                  >
                    <strong>Career Interests:</strong>
                    {{ careerInterestList.join(", ") }}
                  </div>
                  <div
                    v-if="personalInterestList.length > 0"
                    class="text-body-2 mb-2"
                  >
                    <strong>Personal Interests:</strong>
                    {{ personalInterestList.join(", ") }}
                  </div>
                </div>
              </div>

              <!-- Other Items (catch-all "anything else we forgot" data) -->
              <div
                v-if="competencyPoints.length > 0"
                class="export-section mb-5"
              >
                <div class="export-section-header">
                  <v-icon small color="primary" class="mr-2"
                    >mdi-star-outline</v-icon
                  >
                  <span class="text-subtitle-1 font-weight-bold primary--text"
                    >Other Items</span
                  >
                </div>
                <div class="export-section-body">
                  <div
                    v-for="(point, pIdx) in competencyPoints"
                    :key="'comp-' + pIdx"
                    class="text-body-2 mb-2"
                    style="line-height: 1.6;"
                  >
                    <span v-if="point.label" class="font-weight-medium">{{
                      point.label
                    }}</span>
                    <span v-if="point.label && point.detail">: </span>
                    <span>{{ point.detail || point.label }}</span>
                  </div>
                </div>
              </div>

              <!-- Skills -->
              <div v-if="hasSkills" class="export-section mb-5">
                <div class="export-section-header">
                  <v-icon small color="primary" class="mr-2"
                    >mdi-cog-outline</v-icon
                  >
                  <span class="text-subtitle-1 font-weight-bold primary--text"
                    >Skills</span
                  >
                </div>
                <div class="export-section-body">
                  <div v-if="skillsByLevel.advanced.length > 0" class="mb-3">
                    <div class="text-body-2 font-weight-medium mb-1">
                      Technical — Advanced
                    </div>
                    <div class="d-flex flex-wrap">
                      <v-chip
                        v-for="s in skillsByLevel.advanced"
                        :key="s"
                        x-small
                        color="primary"
                        outlined
                        class="mr-1 mb-1"
                        >{{ s }}</v-chip
                      >
                    </div>
                  </div>
                  <div v-if="skillsByLevel.familiar.length > 0" class="mb-3">
                    <div class="text-body-2 font-weight-medium mb-1">
                      Technical — Familiar
                    </div>
                    <div class="d-flex flex-wrap">
                      <v-chip
                        v-for="s in skillsByLevel.familiar"
                        :key="s"
                        x-small
                        outlined
                        class="mr-1 mb-1"
                        >{{ s }}</v-chip
                      >
                    </div>
                  </div>
                  <div v-if="skillsByLevel.basic.length > 0" class="mb-3">
                    <div class="text-body-2 font-weight-medium mb-1">
                      Technical — Basic
                    </div>
                    <div class="d-flex flex-wrap">
                      <v-chip
                        v-for="s in skillsByLevel.basic"
                        :key="s"
                        x-small
                        color="grey"
                        outlined
                        class="mr-1 mb-1"
                        >{{ s }}</v-chip
                      >
                    </div>
                  </div>
                  <div v-if="softSkillList.length > 0" class="mb-3">
                    <div class="text-body-2 font-weight-medium mb-1">
                      Leadership & Soft Skills
                    </div>
                    <div class="d-flex flex-wrap">
                      <v-chip
                        v-for="s in softSkillList"
                        :key="s"
                        x-small
                        color="amber darken-2"
                        outlined
                        class="mr-1 mb-1"
                        >{{ s }}</v-chip
                      >
                    </div>
                  </div>
                </div>
              </div>

              <!-- Professional Experience -->
              <div v-if="workEntries.length > 0" class="export-section mb-5">
                <div class="export-section-header">
                  <v-icon small color="primary" class="mr-2"
                    >mdi-briefcase-outline</v-icon
                  >
                  <span class="text-subtitle-1 font-weight-bold primary--text"
                    >Professional Experience</span
                  >
                  <span class="text-caption text--secondary ml-2"
                    >({{ workEntries.length }} position{{
                      workEntries.length !== 1 ? "s" : ""
                    }})</span
                  >
                </div>
                <div class="export-section-body">
                  <div
                    v-for="(work, wIdx) in workEntries"
                    :key="'work-' + wIdx"
                    class="export-work-item"
                    :class="{ 'mt-4 pt-4 export-work-divider': wIdx > 0 }"
                  >
                    <div
                      class="d-flex justify-space-between align-start flex-wrap"
                    >
                      <div>
                        <div class="text-subtitle-1 font-weight-bold">
                          {{ getWorkPositions(work) }}
                        </div>
                        <div class="text-body-2 font-weight-medium">
                          {{ getWorkCompany(work) }}
                        </div>
                      </div>
                      <div class="text-body-2 text--secondary text-right">
                        <div>
                          {{
                            formatDateRange(
                              getWorkGeneral(work).startDate,
                              getWorkGeneral(work).endDate,
                              getWorkGeneral(work).workingCurrently
                            )
                          }}
                        </div>
                        <div v-if="getWorkLocation(work)">
                          {{ getWorkLocation(work) }}
                        </div>
                      </div>
                    </div>
                    <div
                      v-if="getWorkGeneral(work).industry"
                      class="text-caption text--secondary mt-1"
                    >
                      <v-icon x-small class="mr-1">mdi-domain</v-icon>
                      {{ getWorkGeneral(work).industry }}
                    </div>
                    <div
                      v-if="getWorkDescription(work)"
                      class="text-body-2 mt-2"
                      style="line-height: 1.6;"
                    >
                      {{ getWorkDescription(work) }}
                    </div>
                    <div
                      v-if="getWorkDirectReports(work)"
                      class="text-caption text--secondary mt-1"
                    >
                      <v-icon x-small class="mr-1"
                        >mdi-account-group-outline</v-icon
                      >
                      {{ getWorkDirectReports(work) }} direct report{{
                        getWorkDirectReports(work) !== 1 ? "s" : ""
                      }}
                    </div>

                    <div
                      v-if="getWorkAchievements(work).length > 0"
                      class="mt-3"
                    >
                      <div class="text-body-2 font-weight-medium mb-2">
                        Accomplishments
                      </div>
                      <div
                        v-for="(ach, aIdx) in getWorkAchievements(work)"
                        :key="'ach-' + wIdx + '-' + aIdx"
                        class="export-achievement-item mb-3"
                      >
                        <div class="text-body-2 font-weight-medium">
                          {{ aIdx + 1 }}. {{ getAchievementDisplay(ach) }}
                        </div>
                        <div
                          v-if="getStarSummary(ach)"
                          class="export-star-field"
                        >
                          <span class="export-star-label">Summary:</span>
                          {{ getStarSummary(ach) }}
                        </div>
                        <div v-if="ach.background" class="export-star-field">
                          <span class="export-star-label"
                            >Situation/Context:</span
                          >
                          {{ ach.background }}
                        </div>
                        <div
                          v-if="ach.responsibility"
                          class="export-star-field"
                        >
                          <span class="export-star-label"
                            >Task/Responsibility:</span
                          >
                          {{ ach.responsibility }}
                        </div>
                        <div v-if="ach.actionResult" class="export-star-field">
                          <span class="export-star-label"
                            >Action & Result:</span
                          >
                          {{ ach.actionResult }}
                        </div>
                        <div v-if="ach.measurement" class="export-star-field">
                          <span class="export-star-label"
                            >Measurable Impact:</span
                          >
                          {{ ach.measurement }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Education -->
              <div
                v-if="educationEntries.length > 0"
                class="export-section mb-5"
              >
                <div class="export-section-header">
                  <v-icon small color="primary" class="mr-2"
                    >mdi-school-outline</v-icon
                  >
                  <span class="text-subtitle-1 font-weight-bold primary--text"
                    >Education</span
                  >
                  <span class="text-caption text--secondary ml-2"
                    >({{ educationEntries.length }} entr{{
                      educationEntries.length !== 1 ? "ies" : "y"
                    }})</span
                  >
                </div>
                <div class="export-section-body">
                  <div
                    v-for="(edu, eIdx) in educationEntries"
                    :key="'edu-' + eIdx"
                    class="export-edu-item"
                    :class="{ 'mt-4 pt-4 export-work-divider': eIdx > 0 }"
                  >
                    <div
                      class="d-flex justify-space-between align-start flex-wrap"
                    >
                      <div>
                        <div class="text-subtitle-1 font-weight-bold">
                          {{ getEduDegree(edu) }}
                        </div>
                        <div class="text-body-2 font-weight-medium">
                          {{ getEduSchool(edu) }}
                        </div>
                      </div>
                      <div class="text-body-2 text--secondary text-right">
                        <div>
                          {{
                            formatDateRange(
                              getEduGeneral(edu).dateStart,
                              getEduGeneral(edu).dateEnd
                            )
                          }}
                        </div>
                        <div v-if="getEduLocation(edu)">
                          {{ getEduLocation(edu) }}
                        </div>
                      </div>
                    </div>
                    <div class="d-flex flex-wrap mt-1">
                      <v-chip
                        v-if="getEduGeneral(edu).graduated"
                        x-small
                        color="success"
                        class="mr-2 mb-1"
                        >Graduated</v-chip
                      >
                      <v-chip
                        v-else
                        x-small
                        color="warning"
                        outlined
                        class="mr-2 mb-1"
                        >In Progress</v-chip
                      >
                      <v-chip
                        v-if="getEduGpa(edu)"
                        x-small
                        outlined
                        class="mr-2 mb-1"
                        >GPA: {{ getEduGpa(edu) }}</v-chip
                      >
                      <v-chip
                        v-if="getEduMinor(edu)"
                        x-small
                        outlined
                        class="mr-2 mb-1"
                        >Minor: {{ getEduMinor(edu) }}</v-chip
                      >
                    </div>
                    <div v-if="getEduTests(edu).length > 0" class="mt-2">
                      <div
                        class="text-caption font-weight-medium text--secondary"
                      >
                        Tests
                      </div>
                      <div
                        v-for="(test, tIdx) in getEduTests(edu)"
                        :key="'test-' + tIdx"
                        class="text-body-2"
                      >
                        {{ test.testName
                        }}<span v-if="test.testScore"
                          >: {{ test.testScore }}</span
                        >
                      </div>
                    </div>
                    <div v-if="getEduAwards(edu).length > 0" class="mt-2">
                      <div
                        v-for="(awardGroup, agIdx) in getEduAwards(edu)"
                        :key="'ag-' + agIdx"
                      >
                        <div
                          class="text-caption font-weight-medium text--secondary"
                        >
                          {{ awardGroup.label }}
                        </div>
                        <div
                          v-for="(item, aiIdx) in awardGroup.items"
                          :key="'ai-' + aiIdx"
                          class="text-body-2 ml-3"
                        >
                          &bull; {{ item }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Licenses & Certifications -->
              <div
                v-if="certList.length > 0 || structuredCredentials.length > 0"
                class="export-section mb-5"
              >
                <div class="export-section-header">
                  <v-icon small color="primary" class="mr-2"
                    >mdi-certificate-outline</v-icon
                  >
                  <span class="text-subtitle-1 font-weight-bold primary--text"
                    >Licenses & Certifications</span
                  >
                </div>
                <div class="export-section-body">
                  <div
                    v-for="(cert, cIdx) in certList"
                    :key="'cert-' + cIdx"
                    class="text-body-2 ml-3 mb-1"
                  >
                    &bull; {{ cert }}
                  </div>
                  <template v-if="structuredCredentials.length > 0">
                    <div v-if="certList.length > 0" class="mt-2 mb-1">
                      <span
                        class="text-caption font-weight-bold text--secondary"
                        >Structured Credentials</span
                      >
                    </div>
                    <div
                      v-for="(cred, crIdx) in structuredCredentials"
                      :key="'cred-' + crIdx"
                      class="text-body-2 ml-3 mb-1"
                    >
                      &bull; <strong>{{ cred.name }}</strong>
                      <span
                        v-if="cred.credentialType || cred.credential_type"
                        class="text-caption text--secondary ml-1"
                        >({{
                          cred.credentialType || cred.credential_type
                        }})</span
                      >
                      <span
                        v-if="cred.issuingAuthority || cred.issuing_authority"
                        class="text-caption text--secondary"
                      >
                        —
                        {{
                          cred.issuingAuthority || cred.issuing_authority
                        }}</span
                      >
                      <span
                        v-if="credentialExportStatus(cred)"
                        class="text-caption ml-1"
                        :style="{ color: credentialExportStatusColor(cred) }"
                        >{{ credentialExportStatus(cred) }}</span
                      >
                      <span
                        v-if="cred.jurisdiction"
                        class="text-caption text--secondary ml-1"
                        >[{{ cred.jurisdiction }}]</span
                      >
                    </div>
                  </template>
                </div>
              </div>

              <!-- Languages -->
              <div v-if="languageList.length > 0" class="export-section mb-5">
                <div class="export-section-header">
                  <v-icon small color="primary" class="mr-2"
                    >mdi-translate</v-icon
                  >
                  <span class="text-subtitle-1 font-weight-bold primary--text"
                    >Languages</span
                  >
                </div>
                <div class="export-section-body">
                  <div
                    v-for="(lang, lIdx) in languageList"
                    :key="'lang-' + lIdx"
                    class="text-body-2 ml-3 mb-1"
                  >
                    &bull; {{ lang }}
                  </div>
                </div>
              </div>

              <!-- Professional Organizations -->
              <div v-if="profOrgList.length > 0" class="export-section mb-5">
                <div class="export-section-header">
                  <v-icon small color="primary" class="mr-2"
                    >mdi-account-group-outline</v-icon
                  >
                  <span class="text-subtitle-1 font-weight-bold primary--text"
                    >Professional Organizations</span
                  >
                </div>
                <div class="export-section-body">
                  <div
                    v-for="(org, oIdx) in profOrgList"
                    :key="'porg-' + oIdx"
                    class="text-body-2 ml-3 mb-1"
                  >
                    &bull; {{ org }}
                  </div>
                </div>
              </div>

              <!-- Volunteer & Community Leadership -->
              <div v-if="volOrgList.length > 0" class="export-section mb-5">
                <div class="export-section-header">
                  <v-icon small color="primary" class="mr-2"
                    >mdi-hand-heart-outline</v-icon
                  >
                  <span class="text-subtitle-1 font-weight-bold primary--text"
                    >Volunteer & Community Leadership</span
                  >
                </div>
                <div class="export-section-body">
                  <div
                    v-for="(org, oIdx) in volOrgList"
                    :key="'vorg-' + oIdx"
                    class="text-body-2 ml-3 mb-1"
                  >
                    &bull; {{ org }}
                  </div>
                </div>
              </div>

              <!-- Verified Achievements -->
              <div
                v-if="verifiedSmallWins.length > 0"
                class="export-section mb-5"
              >
                <div class="export-section-header">
                  <v-icon small color="primary" class="mr-2"
                    >mdi-trophy-outline</v-icon
                  >
                  <span class="text-subtitle-1 font-weight-bold primary--text"
                    >Verified Achievements</span
                  >
                  <span class="text-caption text--secondary ml-2"
                    >({{ verifiedSmallWins.length }})</span
                  >
                </div>
                <div class="export-section-body">
                  <div
                    v-for="(item, swIdx) in verifiedSmallWins"
                    :key="'sw-' + swIdx"
                    class="export-win-item mb-3"
                  >
                    <div class="d-flex align-start">
                      <div class="export-win-number mr-3">{{ swIdx + 1 }}</div>
                      <div class="flex-grow-1">
                        <div class="text-body-1 font-weight-medium">
                          {{ getWinSummary(item) }}
                        </div>
                        <div
                          v-if="getWinDraft(item).situation"
                          class="export-star-field"
                        >
                          <span class="export-star-label">Situation:</span>
                          {{ getWinDraft(item).situation }}
                        </div>
                        <div
                          v-if="getWinDraft(item).task"
                          class="export-star-field"
                        >
                          <span class="export-star-label">Task:</span>
                          {{ getWinDraft(item).task }}
                        </div>
                        <div
                          v-if="getWinDraft(item).action"
                          class="export-star-field"
                        >
                          <span class="export-star-label">Action:</span>
                          {{ getWinDraft(item).action }}
                        </div>
                        <div
                          v-if="getWinDraft(item).result"
                          class="export-star-field"
                        >
                          <span class="export-star-label">Result:</span>
                          {{ getWinDraft(item).result }}
                        </div>
                        <div class="d-flex flex-wrap mt-1">
                          <v-chip
                            v-if="
                              item.linkedTo && getLinkedJobLabel(item.linkedTo)
                            "
                            x-small
                            color="teal"
                            outlined
                            class="mr-1 mb-1"
                          >
                            <v-icon x-small left>mdi-link</v-icon>
                            {{ getLinkedJobLabel(item.linkedTo) }}
                          </v-chip>
                          <v-chip
                            v-if="item.promotedTo"
                            x-small
                            color="success"
                            outlined
                            class="mr-1 mb-1"
                          >
                            <v-icon x-small left>mdi-arrow-up-circle</v-icon>
                            On Experience
                          </v-chip>
                          <span
                            v-if="item.createdAt"
                            class="text-caption text--secondary ml-auto"
                            >{{ formatSingleDate(item.createdAt) }}</span
                          >
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Additional Achievements & Notes -->
              <div v-if="allSmallWins.length > 0" class="export-section mb-5">
                <div class="export-section-header">
                  <v-icon small color="primary" class="mr-2"
                    >mdi-note-text-outline</v-icon
                  >
                  <span class="text-subtitle-1 font-weight-bold primary--text"
                    >Additional Achievements & Notes</span
                  >
                  <span class="text-caption text--secondary ml-2"
                    >({{ allSmallWins.length }})</span
                  >
                </div>
                <div class="export-section-body">
                  <div
                    v-for="(item, swIdx) in allSmallWins"
                    :key="'allsw-' + swIdx"
                    class="mb-2"
                  >
                    <div class="text-body-2">
                      <v-chip
                        x-small
                        :color="
                          item.parsedType === 'achievement' ? 'success' : 'grey'
                        "
                        outlined
                        class="mr-2"
                        >{{ item.parsedType || "note" }}</v-chip
                      >
                      {{ item.rawInput || item.description || "" }}
                    </div>
                    <div
                      v-if="item.linkedTo && getLinkedJobLabel(item.linkedTo)"
                      class="text-caption text--secondary ml-8"
                    >
                      Linked: {{ getLinkedJobLabel(item.linkedTo) }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Personal Interests -->
              <div
                v-if="personalInterestList.length > 0"
                class="export-section mb-5"
              >
                <div class="export-section-header">
                  <v-icon small color="primary" class="mr-2"
                    >mdi-heart-outline</v-icon
                  >
                  <span class="text-subtitle-1 font-weight-bold primary--text"
                    >Personal Interests</span
                  >
                </div>
                <div class="export-section-body">
                  <div
                    v-for="(interest, iIdx) in personalInterestList"
                    :key="'pint-' + iIdx"
                    class="text-body-2 ml-3 mb-1"
                  >
                    &bull; {{ interest }}
                  </div>
                </div>
              </div>

              <!-- Workplace Fit Preferences -->
              <div v-if="hasFitData" class="export-section mb-5">
                <div class="export-section-header">
                  <v-icon small color="primary" class="mr-2">mdi-tune</v-icon>
                  <span class="text-subtitle-1 font-weight-bold primary--text"
                    >Workplace Fit Preferences</span
                  >
                </div>
                <div class="export-section-body">
                  <p class="text-caption text--secondary mb-3">
                    Work environments, culture, and conditions where this person
                    thrives.
                  </p>
                  <div
                    v-for="(answer, fIdx) in fitDisplayAnswers"
                    :key="'fit-' + fIdx"
                    class="mb-2"
                  >
                    <div class="text-body-2 font-weight-medium">
                      {{ fIdx + 1 }}. {{ answer.question }}
                    </div>
                    <div class="text-body-2 ml-4">{{ answer.response }}</div>
                  </div>
                  <!-- PR-FIT-NOTES-FIX: additional-context textarea answer
                       lives here, inside Fit & Preferences, instead of
                       being mislabeled as a "Personal Statement" up top. -->
                  <div v-if="fitAdditionalNotes" class="mt-3 pt-3 fit-notes-block">
                    <div class="text-body-2 font-weight-medium mb-1">
                      Additional Context
                    </div>
                    <div class="text-body-2 ml-4" style="white-space: pre-wrap;">
                      {{ fitAdditionalNotes }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Career Goals & Interests -->
              <div v-if="hasCareerGoals" class="export-section mb-5">
                <div class="export-section-header">
                  <v-icon small color="primary" class="mr-2"
                    >mdi-rocket-launch-outline</v-icon
                  >
                  <span class="text-subtitle-1 font-weight-bold primary--text"
                    >Career Goals & Interests</span
                  >
                </div>
                <div class="export-section-body">
                  <div
                    v-if="targetIndustry || industryListDisplay.length > 0"
                    class="mb-3"
                  >
                    <div v-if="targetIndustry" class="text-body-2">
                      <strong>Target Industry:</strong> {{ targetIndustry }}
                    </div>
                    <div
                      v-if="industryListDisplay.length > 0"
                      class="text-body-2"
                    >
                      <strong>Industries:</strong>
                      {{ industryListDisplay.join(", ") }}
                    </div>
                    <div v-if="industryReasons" class="text-body-2">
                      <strong>Why:</strong> {{ industryReasons }}
                    </div>
                  </div>
                  <div v-if="targetCompany || targetRole" class="mb-3">
                    <div v-if="targetCompany" class="text-body-2">
                      <strong>Target Company:</strong> {{ targetCompany }}
                    </div>
                    <div v-if="companyDoes" class="text-body-2">
                      <strong>What They Do:</strong> {{ companyDoes }}
                    </div>
                    <div v-if="targetRole" class="text-body-2">
                      <strong>Target Role:</strong> {{ targetRole }}
                    </div>
                    <div v-if="companyReasons" class="text-body-2">
                      <strong>Why:</strong> {{ companyReasons }}
                    </div>
                  </div>
                  <div v-if="careerInterestList.length > 0" class="mb-2">
                    <div class="text-body-2">
                      <strong>Career Interests:</strong>
                      {{ careerInterestList.join(", ") }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Empty State -->
              <div v-if="!hasAnyData" class="text-center py-12">
                <v-icon size="64" color="grey lighten-1"
                  >mdi-database-off-outline</v-icon
                >
                <p class="text-body-1 text--secondary mt-4">
                  Your career profile is empty. Start adding information to see
                  it here.
                </p>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>

      <v-snackbar v-model="snackbar" :timeout="3000" :color="snackbarColor">
        {{ snackbarText }}
      </v-snackbar>
    </v-container>
  </page-main>
</template>

<script>
import PageMain from "@/components/page/PageMain.vue";
import { mapState } from "vuex";
import { generateCareerDossier } from "@/utility/careerDossierGenerator";
import { readSuperpowersRecord } from "@/utility/superpowers";

export default {
  name: "ProfileExport",
  components: {
    PageMain
  },
  data: function() {
    return {
      error: "",
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
      exportingDocx: false
    };
  },
  mounted: function() {
    this.$store.dispatch("fitPreferences/loadFitPreferencesQuestions");
    this.$store.dispatch("fitPreferences/loadFitPreferences");
  },
  computed: {
    ...mapState(["resumeId"]),
    ...mapState("name", [
      "displayValue",
      "firstName",
      "middleName",
      "lastName",
      "suffix",
      "title"
    ]),
    ...mapState("address", [
      "email",
      "phoneNumber",
      "linkedin",
      "otherLink",
      "addressLine1",
      "addressLine2",
      "city",
      "state",
      "country",
      "zipCode"
    ]),
    ...mapState("work", ["workDict"]),
    ...mapState("education", ["educationDict"]),
    ...mapState("hardSkill", {
      hardSkills: "hardSkills",
      hardSkillLabels: "hardSkillLabels"
    }),
    ...mapState("softSkill", { softSkills: "softSkills" }),
    ...mapState("language", {
      languages: "languages",
      languagesDisplay: "languagesDisplay"
    }),
    ...mapState("licenseCert", {
      licensesCerts: "licensesCerts",
      licensesCertsDisplay: "licensesCertsDisplay",
      vuexCredentials: "credentials"
    }),
    ...mapState("careerInterest", {
      careerInterests: "careerInterests",
      structuredCareerInterests: "structuredCareerInterests"
    }),
    ...mapState("personalInterest", {
      personalInterests: "personalInterests",
      structuredPersonalInterests: "structuredPersonalInterests"
    }),
    ...mapState("adjective", {
      selectedAdjectives: "selectedAdjectives",
      adjectiveDisplay: "adjectiveDisplay"
    }),
    ...mapState("role", {
      selectedRoles: "selectedRoles",
      roleDisplay: "roleDisplay"
    }),
    ...mapState("organization", [
      "professionalOrg",
      "professionalOrgDisplay",
      "volunteerOrg",
      "volunteerOrgDisplay"
    ]),
    ...mapState("finalInfo", [
      "professionalSummary",
      "otherSkills",
      "otherNotes",
      "points"
    ]),
    ...mapState("targetIndustry", {
      targetIndustry: "targetIndustry",
      industries: "industries",
      industrySkills: "industrySkills",
      industryReasons: "industryReasons"
    }),
    ...mapState("targetCompany", [
      "targetCompany",
      "companyDoes",
      "targetRole",
      "companyReasons",
      "companyGoals"
    ]),
    ...mapState("smallWins", { smallWinsItems: "items" }),
    ...mapState("fitPreferences", {
      fitAnswers: "answers",
      fitAdditionalNotes: "additionalNotes",
      fitQuestions: "questions"
    }),

    workCount() {
      return Object.values(this.workDict || {}).filter(Boolean).length;
    },
    educationCount() {
      return Object.values(this.educationDict || {}).filter(Boolean).length;
    },
    skillCount() {
      return (this.hardSkills || []).length + (this.softSkills || []).length;
    },
    verifiedSmallWins() {
      return (this.smallWinsItems || []).filter(function(item) {
        return item.status === "verified";
      });
    },
    smallWinsCount() {
      return this.verifiedSmallWins.length;
    },
    allSmallWins() {
      return (this.smallWinsItems || []).filter(function(item) {
        return item.status !== "verified";
      });
    },
    // SUPERPOWERS: read the ONE shared superpowers record for this profile from
    // localStorage (single key resumation_superpowers_<resumeId>, migrating
    // gracefully from any old per-hash entry) via the shared helper. The export
    // does NOT generate (generation lives on the Career Profile and the studio);
    // it only includes what is already there. Honest-empty: no record means the
    // export simply omits the Superpowers section.
    cachedSuperpowers() {
      var rec = readSuperpowersRecord(this.resumeId);
      return rec ? rec.superpowers : [];
    },
    profileStats() {
      var parts = [];
      if (this.workCount > 0)
        parts.push(this.workCount + " job" + (this.workCount !== 1 ? "s" : ""));
      if (this.educationCount > 0)
        parts.push(this.educationCount + " education");
      if (this.skillCount > 0)
        parts.push(
          this.skillCount + " skill" + (this.skillCount !== 1 ? "s" : "")
        );
      if (this.smallWinsCount > 0)
        parts.push(
          this.smallWinsCount + " win" + (this.smallWinsCount !== 1 ? "s" : "")
        );
      return parts.length > 0
        ? parts.join(", ")
        : "Start building your profile";
    },
    contactName() {
      return (
        this.displayValue ||
        [this.firstName, this.middleName, this.lastName, this.suffix]
          .filter(Boolean)
          .join(" ")
          .trim()
      );
    },
    contactTitle() {
      return this.title || "";
    },
    exportDate() {
      return new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric"
      });
    },
    hasContactData() {
      return !!(
        this.email ||
        this.phoneNumber ||
        this.linkedin ||
        this.otherLink ||
        this.contactLocation
      );
    },
    contactLocation() {
      var parts = [];
      if (this.addressLine1) parts.push(this.addressLine1);
      if (this.addressLine2) parts.push(this.addressLine2);
      var locParts = [this.city, this.state, this.zipCode].filter(Boolean);
      if (locParts.length > 0) parts.push(locParts.join(", "));
      if (this.country) parts.push(this.country);
      return parts.join(", ");
    },
    hasIdentityData() {
      return (
        this.roleList.length > 0 ||
        this.adjectiveList.length > 0 ||
        this.careerInterestList.length > 0 ||
        this.personalInterestList.length > 0
      );
    },
    hasRoles() {
      return this.roleList.length > 0;
    },
    personalInterestList() {
      // PR-PROFILE-STORY-POLISH: include BOTH the structured personal
      // interests (PersonalInterestEntry[], keyed on .name) and the legacy
      // StringItem list (.text). The rich form writes to the structured
      // slice, so reading only the legacy list dropped most users' data.
      var structured = (this.structuredPersonalInterests || [])
        .map(function(p) {
          return p.name || p.text || p;
        })
        .filter(Boolean);
      var legacy = (this.personalInterests || [])
        .map(function(p) {
          return p.text || p;
        })
        .filter(Boolean);
      var seen = {};
      return structured.concat(legacy).filter(function(item) {
        var key = String(item).toLowerCase();
        if (seen[key]) return false;
        seen[key] = true;
        return true;
      });
    },
    roleList() {
      return (this.selectedRoles || [])
        .map(function(r) {
          return r.text || r;
        })
        .filter(Boolean);
    },
    adjectiveList() {
      return (this.selectedAdjectives || [])
        .map(function(a) {
          return a.text || a;
        })
        .filter(Boolean);
    },
    workEntries() {
      var entries = Object.values(this.workDict || {}).filter(Boolean);
      entries.sort(function(a, b) {
        return (a.order || 0) - (b.order || 0);
      });
      return entries;
    },
    educationEntries() {
      var entries = Object.values(this.educationDict || {}).filter(Boolean);
      entries.sort(function(a, b) {
        return (a.order || 0) - (b.order || 0);
      });
      return entries;
    },
    hasSkills() {
      return (
        (this.hardSkills || []).length > 0 || (this.softSkills || []).length > 0
      );
    },
    skillsByLevel() {
      var hard = this.hardSkills || [];
      return {
        advanced: hard
          .filter(function(s) {
            return s.level === 1;
          })
          .map(function(s) {
            return s.text;
          })
          .filter(Boolean),
        familiar: hard
          .filter(function(s) {
            return s.level === 2;
          })
          .map(function(s) {
            return s.text;
          })
          .filter(Boolean),
        basic: hard
          .filter(function(s) {
            return s.level === 3;
          })
          .map(function(s) {
            return s.text;
          })
          .filter(Boolean)
      };
    },
    softSkillList() {
      return (this.softSkills || [])
        .map(function(s) {
          return s.text || s;
        })
        .filter(Boolean);
    },
    languageList() {
      return (this.languages || [])
        .map(function(l) {
          var text = l.language || l.text || l;
          var proficiency = l.languageProficiency || l.level || "";
          return proficiency ? text + " (" + proficiency + ")" : text;
        })
        .filter(Boolean);
    },
    certList() {
      return (this.licensesCerts || [])
        .map(function(c) {
          return c.text || c;
        })
        .filter(Boolean);
    },
    structuredCredentials() {
      return this.vuexCredentials || [];
    },
    hasFitData() {
      // PR-FIT-NOTES-FIX: additional-context notes also light up the Fit
      // section so a user who only wrote a note (no question answers)
      // still sees their content rendered, instead of it disappearing.
      return (
        (this.fitAnswers || []).length > 0 || !!this.fitAdditionalNotes
      );
    },
    fitDisplayAnswers() {
      var answers = this.fitAnswers || [];
      var questions = this.fitQuestions || [];
      return answers
        .map(function(answer, index) {
          var question = questions.find(function(q) {
            return q.id === answer.questionId;
          });
          var questionText = question
            ? question.label
            : "Preference " + (answer.questionId || index + 1);
          var selectedLabels = (answer.selectedOptions || [])
            .map(function(opt) {
              return opt.label || opt.text || opt;
            })
            .filter(Boolean);
          return {
            question: questionText,
            response: selectedLabels.join(", ")
          };
        })
        .filter(function(a) {
          return a.response;
        });
    },
    hasCareerGoals() {
      return !!(
        this.targetIndustry ||
        this.targetCompany ||
        this.targetRole ||
        (this.industries && this.industries.length > 0) ||
        this.careerInterestList.length > 0
      );
    },
    industryListDisplay() {
      return (this.industries || [])
        .map(function(i) {
          return i.text || i;
        })
        .filter(Boolean);
    },
    careerInterestList() {
      // PR-PROFILE-STORY-POLISH: include BOTH the structured career interests
      // (keyed on .name) and the legacy StringItem list (.text), same as the
      // personal-interest merge above.
      var structured = (this.structuredCareerInterests || [])
        .map(function(c) {
          return c.name || c.text || c;
        })
        .filter(Boolean);
      var legacy = (this.careerInterests || [])
        .map(function(c) {
          return c.text || c;
        })
        .filter(Boolean);
      var seen = {};
      return structured.concat(legacy).filter(function(item) {
        var key = String(item).toLowerCase();
        if (seen[key]) return false;
        seen[key] = true;
        return true;
      });
    },
    hasOrganizations() {
      return this.profOrgList.length > 0 || this.volOrgList.length > 0;
    },
    profOrgList() {
      var orgs = this.professionalOrg || [];
      if (orgs.length > 0)
        return orgs
          .map(function(o) {
            return o.text || o;
          })
          .filter(Boolean);
      if (this.professionalOrgDisplay) return [this.professionalOrgDisplay];
      return [];
    },
    volOrgList() {
      var orgs = this.volunteerOrg || [];
      if (orgs.length > 0)
        return orgs
          .map(function(o) {
            return o.text || o;
          })
          .filter(Boolean);
      if (this.volunteerOrgDisplay) return [this.volunteerOrgDisplay];
      return [];
    },
    competencyPoints() {
      var pts = this.points || [];
      return pts
        .map(function(p) {
          var text = p.text || p;
          if (!text) return null;
          var colonIdx = text.indexOf(":");
          if (colonIdx > 0 && colonIdx < 60) {
            return {
              label: text.substring(0, colonIdx).trim(),
              detail: text.substring(colonIdx + 1).trim()
            };
          }
          return { label: "", detail: text };
        })
        .filter(Boolean);
    },
    hasAnyData() {
      return (
        this.hasContactData ||
        !!this.professionalSummary ||
        !!this.fitAdditionalNotes ||
        this.competencyPoints.length > 0 ||
        this.hasIdentityData ||
        this.workEntries.length > 0 ||
        this.educationEntries.length > 0 ||
        this.hasSkills ||
        this.certList.length > 0 ||
        this.languageList.length > 0 ||
        this.profOrgList.length > 0 ||
        this.volOrgList.length > 0 ||
        this.verifiedSmallWins.length > 0 ||
        this.allSmallWins.length > 0 ||
        this.personalInterestList.length > 0 ||
        this.hasFitData ||
        this.hasCareerGoals
      );
    },
    includedSections() {
      return [
        {
          label: "Contact Information",
          hasData: !!(this.displayValue || this.email)
        },
        { label: "Professional Summary", hasData: !!this.professionalSummary },
        { label: "Superpowers", hasData: this.cachedSuperpowers.length > 0 },
        // PR-FIT-NOTES-FIX: "Personal Statement" entry removed — that
        // section no longer exists; its data lives inside Fit & Preferences
        // as the Additional Context block.
        {
          label: "Identity & Career Positioning",
          hasData: this.hasIdentityData
        },
        { label: "Other Items", hasData: (this.points || []).length > 0 },
        {
          label: "Skills",
          hasData:
            (this.hardSkills || []).length > 0 ||
            (this.softSkills || []).length > 0
        },
        { label: "Professional Experience", hasData: this.workCount > 0 },
        { label: "Education", hasData: this.educationCount > 0 },
        {
          label: "Licenses & Certifications",
          hasData:
            this.certList.length > 0 || this.structuredCredentials.length > 0
        },
        { label: "Languages", hasData: this.languageList.length > 0 },
        {
          label: "Professional Organizations",
          hasData: this.profOrgList.length > 0
        },
        {
          label: "Volunteer & Community Leadership",
          hasData: this.volOrgList.length > 0
        },
        { label: "Verified Achievements", hasData: this.smallWinsCount > 0 },
        {
          label: "Additional Achievements & Notes",
          hasData: this.allSmallWins.length > 0
        },
        {
          label: "Personal Interests",
          hasData: this.personalInterestList.length > 0
        },
        {
          label: "Workplace Fit Preferences",
          hasData: (this.fitAnswers || []).length > 0
        },
        {
          label: "Career Goals & Interests",
          hasData: this.hasCareerGoals
        }
      ];
    },
    fullTextExport() {
      var lines = [];
      var sep = "=".repeat(50);
      var subsep = "-".repeat(30);

      lines.push("RESUMATION CAREER PROFILE");
      lines.push(sep);
      lines.push(
        "Exported: " +
          new Date().toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric"
          })
      );
      lines.push("");

      lines.push(this.buildContactSection(subsep));
      lines.push(this.buildProfessionalSummarySection(subsep));
      lines.push(this.buildSuperpowersSection(subsep));
      // PR-FIT-NOTES-FIX: PersonalStatement section removed from text
      // export. The additional-context note is now appended to the
      // Fit Preferences section in buildFitPreferencesSection below.
      lines.push(this.buildRolesSection(subsep));
      lines.push(this.buildKeyCompetenciesSection(subsep));
      lines.push(this.buildSkillsSection(subsep));
      lines.push(this.buildWorkExperienceSection(subsep));
      lines.push(this.buildEducationSection(subsep));
      lines.push(this.buildCertificationsSection(subsep));
      lines.push(this.buildLanguagesSection(subsep));
      lines.push(this.buildProfessionalOrgsSection(subsep));
      lines.push(this.buildVolunteerOrgsSection(subsep));
      lines.push(this.buildAchievementsSection(subsep));
      lines.push(this.buildAllSmallWinsSection(subsep));
      lines.push(this.buildPersonalInterestsSection(subsep));
      lines.push(this.buildFitPreferencesSection(subsep));
      lines.push(this.buildCareerGoalsSection(subsep));
      lines.push(this.buildAdditionalSection(subsep));

      lines.push(sep);
      lines.push("End of Resumation Career Profile");

      return lines
        .filter(function(s) {
          return s !== null;
        })
        .join("\n");
    }
  },
  methods: {
    credentialExportStatus(cred) {
      if (cred.doesNotExpire || cred.does_not_expire) return "Active";
      var exp = cred.expirationDate || cred.expiration_date;
      if (!exp) return cred.status || "";
      var now = new Date();
      var expDate = new Date(exp);
      if (expDate <= now) return "Expired";
      var ninetyDays = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);
      if (expDate <= ninetyDays) return "Expiring Soon";
      return "Active";
    },
    credentialExportStatusColor(cred) {
      var status = this.credentialExportStatus(cred);
      if (status === "Expired") return "#EF4444";
      if (status === "Expiring Soon") return "#F59E0B";
      if (status === "Active") return "#10B981";
      return "#6B7280";
    },
    getWorkGeneral(work) {
      return work.workGeneral || {};
    },
    getWorkCompany(work) {
      var g = work.workGeneral || {};
      return g.companyName || "Unknown Company";
    },
    getWorkPositions(work) {
      var positions = (work.workRole && work.workRole.positions) || [];
      if (positions.length > 0) {
        return positions
          .map(function(p) {
            var str = p.title || "Untitled";
            if (p.role) str += " (" + p.role + ")";
            return str;
          })
          .join(" / ");
      }
      var g = work.workGeneral || {};
      return g.display || "Position";
    },
    getWorkLocation(work) {
      var g = work.workGeneral || {};
      return [g.companyCity, g.companyState, g.companyCountry]
        .filter(Boolean)
        .join(", ");
    },
    getWorkDescription(work) {
      var resp = work.workResponsibilities || {};
      return resp.description || "";
    },
    getWorkDirectReports(work) {
      var resp = work.workResponsibilities || {};
      return resp.directReports || 0;
    },
    getWorkAchievements(work) {
      return work.achievementList || [];
    },
    getAchievementDisplay(ach) {
      return (
        (ach.useCustom ? ach.displayCustom : ach.displayAuto) ||
        ach.displayCustom ||
        ach.displayAuto ||
        "(untitled)"
      );
    },
    getStarSummary(ach) {
      var parts = [
        ach.background,
        ach.responsibility,
        ach.actionResult,
        ach.measurement
      ].filter(Boolean);
      return parts.length > 1 ? parts.join(" ") : "";
    },
    getEduGeneral(edu) {
      return edu.educationGeneral || {};
    },
    getEduSchool(edu) {
      var g = edu.educationGeneral || {};
      return g.universityName || g.schoolName || "Unknown School";
    },
    getEduDegree(edu) {
      var d = edu.educationDegree || {};
      if (d.degreeName) {
        return d.degreeArea
          ? d.degreeName + " in " + d.degreeArea
          : d.degreeName;
      }
      return "Degree";
    },
    getEduLocation(edu) {
      var g = edu.educationGeneral || {};
      return [g.universityCity, g.universityState, g.country]
        .filter(Boolean)
        .join(", ");
    },
    getEduGpa(edu) {
      var gpa = edu.educationGPA || {};
      if (!gpa.gpa) return "";
      return gpa.gpaScale ? gpa.gpa + " / " + gpa.gpaScale : gpa.gpa;
    },
    getEduMinor(edu) {
      var minor = edu.educationDegreeMinor || {};
      return minor.minors || "";
    },
    getEduTests(edu) {
      return (edu.educationTests || []).filter(function(t) {
        return t.testName || t.testScore;
      });
    },
    getEduAwards(edu) {
      var awards = edu.educationAwards || {};
      var groups = [];
      var types = [
        { label: "Activities", items: awards.activities },
        { label: "Awards", items: awards.awards },
        { label: "Honors", items: awards.honors },
        { label: "Projects", items: awards.projects }
      ];
      types.forEach(function(t) {
        if (t.items && t.items.length > 0) {
          var texts = t.items
            .map(function(i) {
              return i.text || i;
            })
            .filter(Boolean);
          if (texts.length > 0) groups.push({ label: t.label, items: texts });
        }
      });
      return groups;
    },
    getWinSummary(item) {
      if (item.aiDraft && item.aiDraft.summary) return item.aiDraft.summary;
      return item.rawInput || item.description || "";
    },
    getWinDraft(item) {
      return item.aiDraft || {};
    },
    buildContactSection(subsep) {
      var lines = [];
      lines.push("CONTACT INFORMATION");
      lines.push(subsep);

      var name =
        this.displayValue ||
        [this.firstName, this.middleName, this.lastName, this.suffix]
          .filter(Boolean)
          .join(" ")
          .trim();
      if (name) lines.push("Name: " + name);
      if (this.title) lines.push("Professional Title: " + this.title);
      if (this.email) lines.push("Email: " + this.email);
      if (this.phoneNumber) lines.push("Phone: " + this.phoneNumber);
      if (this.linkedin) lines.push("LinkedIn: " + this.linkedin);
      if (this.otherLink) lines.push("Website/Portfolio: " + this.otherLink);

      if (this.addressLine1) lines.push("Address: " + this.addressLine1);
      if (this.addressLine2) lines.push("         " + this.addressLine2);
      var locationParts = [this.city, this.state, this.zipCode].filter(Boolean);
      if (locationParts.length > 0 || this.country) {
        var locStr = locationParts.join(", ");
        if (this.country)
          locStr = locStr ? locStr + ", " + this.country : this.country;
        if (!this.addressLine1) {
          lines.push("Location: " + locStr);
        } else {
          lines.push("         " + locStr);
        }
      }

      if (lines.length <= 2) lines.push("No contact information recorded");
      lines.push("");
      return lines.join("\n");
    },
    buildProfessionalSummarySection(subsep) {
      if (!this.professionalSummary) return null;
      var lines = [];
      lines.push("PROFESSIONAL SUMMARY");
      lines.push(subsep);
      lines.push(this.professionalSummary);
      lines.push("");
      return lines.join("\n");
    },
    // SUPERPOWERS: include the cached superpowers (name + one-liner + evidence
    // citations) in the PDF/text export, with the decision-authority disclaimer
    // appended. Returns null (section omitted) when nothing was generated -
    // honest-empty, never fabricated.
    buildSuperpowersSection(subsep) {
      var items = this.cachedSuperpowers;
      if (!items || items.length === 0) return null;

      var lines = [];
      lines.push("SUPERPOWERS");
      lines.push(subsep);

      items.forEach(function(sp, index) {
        if (index > 0) lines.push("");
        lines.push(index + 1 + ". " + (sp.name || "Superpower"));
        if (sp.oneLiner) lines.push("   " + sp.oneLiner);
        if (sp.evidence && sp.evidence.length > 0) {
          lines.push("   Backed by:");
          sp.evidence.forEach(function(ev) {
            if (ev) lines.push("   * " + ev);
          });
        }
        if (sp.roles && sp.roles.length > 0) {
          lines.push("   Where it showed up: " + sp.roles.join(", "));
        }
      });

      lines.push("");
      lines.push(
        "Note: Superpowers are an AI interpretation of this person's own profile evidence - the cited achievements and wins are the facts. They do not make any hiring decision; use them as a starting point and verify against the underlying evidence."
      );
      lines.push("");
      return lines.join("\n");
    },
    buildKeyCompetenciesSection(subsep) {
      var pts = this.points || [];
      if (pts.length === 0) return null;
      var lines = [];
      lines.push("OTHER ITEMS");
      lines.push(subsep);
      pts.forEach(function(p) {
        var text = p.text || p;
        if (text) lines.push("* " + text);
      });
      lines.push("");
      return lines.join("\n");
    },
    buildRolesSection(subsep) {
      var adjectives = (this.selectedAdjectives || [])
        .map(function(a) {
          return a.text || a;
        })
        .filter(Boolean);
      var roles = (this.selectedRoles || [])
        .map(function(r) {
          return r.text || r;
        })
        .filter(Boolean);
      // PR-PROFILE-STORY-POLISH: merged lists (structured + legacy).
      var career = this.careerInterestList;
      var personal = this.personalInterestList;

      if (
        adjectives.length === 0 &&
        roles.length === 0 &&
        career.length === 0 &&
        personal.length === 0
      )
        return null;

      var lines = [];
      lines.push("IDENTITY & CAREER POSITIONING");
      lines.push(subsep);
      if (adjectives.length > 0) {
        lines.push("Professional Descriptors: " + adjectives.join(", "));
      }
      if (roles.length > 0) {
        lines.push("Professional Archetypes: " + roles.join(", "));
      }
      if (career.length > 0) {
        lines.push("Career Interests: " + career.join(", "));
      }
      if (personal.length > 0) {
        lines.push("Personal Interests: " + personal.join(", "));
      }
      lines.push("");
      return lines.join("\n");
    },
    // PR-FIT-NOTES-FIX: buildPersonalStatementSection removed. The
    // additional-context note (formerly mislabeled as "Personal
    // Statement") now appears inside buildFitPreferencesSection where
    // it belongs.
    buildWorkExperienceSection(subsep) {
      var workEntries = Object.values(this.workDict || {}).filter(Boolean);
      var lines = [];
      lines.push("PROFESSIONAL EXPERIENCE");
      lines.push(subsep);

      if (workEntries.length === 0) {
        lines.push("No work experience recorded");
        lines.push("");
        return lines.join("\n");
      }

      workEntries.sort(function(a, b) {
        return (a.order || 0) - (b.order || 0);
      });

      var self = this;
      workEntries.forEach(function(work, index) {
        if (index > 0) {
          lines.push("");
        }

        var general = work.workGeneral || {};
        var company = general.companyName || "Unknown Company";
        var locationParts = [
          general.companyCity,
          general.companyState,
          general.companyCountry
        ].filter(Boolean);
        lines.push(company);
        if (locationParts.length > 0)
          lines.push("Location: " + locationParts.join(", "));
        if (general.industry) lines.push("Industry: " + general.industry);

        var dateStr = self.formatDateRange(
          general.startDate,
          general.endDate,
          general.workingCurrently
        );
        if (dateStr) lines.push("Dates: " + dateStr);

        var positions = (work.workRole && work.workRole.positions) || [];
        if (positions.length > 0) {
          positions.forEach(function(p) {
            var posStr = p.title || "Untitled Position";
            if (p.role) posStr += " (" + p.role + ")";
            lines.push("Position: " + posStr);
          });
        }

        var resp = work.workResponsibilities;
        if (resp) {
          if (resp.description) {
            lines.push("");
            lines.push("Role Description:");
            lines.push("  " + resp.description);
          }
          if (resp.directReports > 0) {
            lines.push("Direct Reports: " + resp.directReports);
          }
        }

        var achievements = work.achievementList || [];
        if (achievements.length > 0) {
          lines.push("");
          lines.push("Key Accomplishments:");
          achievements.forEach(function(ach, achIdx) {
            var display = ach.useCustom ? ach.displayCustom : ach.displayAuto;
            if (display) {
              lines.push("  " + (achIdx + 1) + ". " + display);
            } else {
              lines.push("  " + (achIdx + 1) + ". (accomplishment)");
            }
            var starParts = [
              ach.background,
              ach.responsibility,
              ach.actionResult,
              ach.measurement
            ].filter(Boolean);
            if (starParts.length > 1) {
              lines.push("     Summary: " + starParts.join(" "));
            }
            if (ach.background)
              lines.push("     Situation/Context: " + ach.background);
            if (ach.responsibility)
              lines.push("     Task/Responsibility: " + ach.responsibility);
            if (ach.actionResult)
              lines.push("     Action & Result: " + ach.actionResult);
            if (ach.measurement)
              lines.push("     Measurable Impact: " + ach.measurement);
          });
        }
      });

      lines.push("");
      return lines.join("\n");
    },
    buildEducationSection(subsep) {
      var eduEntries = Object.values(this.educationDict || {}).filter(Boolean);
      var lines = [];
      lines.push("EDUCATION");
      lines.push(subsep);

      if (eduEntries.length === 0) {
        lines.push("No education recorded");
        lines.push("");
        return lines.join("\n");
      }

      eduEntries.sort(function(a, b) {
        return (a.order || 0) - (b.order || 0);
      });

      var self = this;
      eduEntries.forEach(function(edu, index) {
        if (index > 0) {
          lines.push("");
        }

        var general = edu.educationGeneral || {};
        var school =
          general.universityName || general.schoolName || "Unknown School";
        var locationParts = [
          general.universityCity,
          general.universityState,
          general.country
        ].filter(Boolean);

        lines.push(school);
        if (locationParts.length > 0)
          lines.push("Location: " + locationParts.join(", "));

        var degree = edu.educationDegree || {};
        if (degree.degreeName) {
          var degreeStr = degree.degreeName;
          if (degree.degreeArea) degreeStr += " in " + degree.degreeArea;
          lines.push("Degree: " + degreeStr);
        }

        var minor = edu.educationDegreeMinor;
        if (minor && minor.minors) {
          lines.push("Minor: " + minor.minors);
        }

        var gpa = edu.educationGPA;
        if (gpa && gpa.gpa) {
          var gpaStr = gpa.gpa;
          if (gpa.gpaScale) gpaStr += " / " + gpa.gpaScale;
          lines.push("GPA: " + gpaStr);
        }

        var dateStr = self.formatDateRange(
          general.dateStart,
          general.dateEnd,
          false
        );
        if (dateStr) lines.push("Dates Attended: " + dateStr);
        lines.push("Graduated: " + (general.graduated ? "Yes" : "No"));
        if (general.hasExpectedGrad && general.dateExpected) {
          lines.push(
            "Expected Graduation: " +
              self.formatSingleDate(general.dateExpected)
          );
        }

        var tests = edu.educationTests || [];
        if (tests.length > 0) {
          var validTests = tests.filter(function(t) {
            return t.testName || t.testScore;
          });
          if (validTests.length > 0) {
            lines.push("Standardized Tests:");
            validTests.forEach(function(t) {
              var testStr = t.testName || "Test";
              if (t.testScore) testStr += ": " + t.testScore;
              lines.push("  * " + testStr);
            });
          }
        }

        var awards = edu.educationAwards || {};
        var awardTypes = [
          { label: "Activities", items: awards.activities },
          { label: "Awards", items: awards.awards },
          { label: "Honors", items: awards.honors },
          { label: "Projects", items: awards.projects }
        ];
        awardTypes.forEach(function(at) {
          if (at.items && at.items.length > 0) {
            var texts = at.items
              .map(function(i) {
                return i.text || i;
              })
              .filter(Boolean);
            if (texts.length > 0) {
              lines.push(at.label + ":");
              texts.forEach(function(txt) {
                lines.push("  * " + txt);
              });
            }
          }
        });

        var accolades = edu.educationAccolades || [];
        if (accolades.length > 0) {
          lines.push("Accolades:");
          accolades.forEach(function(acc) {
            var display = acc.useCustom
              ? acc.displayCustom
              : acc.displayAuto || acc.text || "";
            if (display) lines.push("  * " + display);
            if (acc.background) lines.push("    Context: " + acc.background);
            if (acc.responsibility)
              lines.push("    Role: " + acc.responsibility);
            if (acc.actionResult)
              lines.push("    Action & Result: " + acc.actionResult);
            if (acc.measurement) lines.push("    Impact: " + acc.measurement);
          });
        }
      });

      lines.push("");
      return lines.join("\n");
    },
    buildSkillsSection(subsep) {
      var hard = this.hardSkills || [];
      var soft = this.softSkills || [];

      if (hard.length === 0 && soft.length === 0) return null;

      var lines = [];
      lines.push("SKILLS");
      lines.push(subsep);

      if (hard.length > 0) {
        var advanced = hard
          .filter(function(s) {
            return s.level === 1;
          })
          .map(function(s) {
            return s.text;
          })
          .filter(Boolean);
        var familiar = hard
          .filter(function(s) {
            return s.level === 2;
          })
          .map(function(s) {
            return s.text;
          })
          .filter(Boolean);
        var basic = hard
          .filter(function(s) {
            return s.level === 3;
          })
          .map(function(s) {
            return s.text;
          })
          .filter(Boolean);

        if (advanced.length > 0)
          lines.push("Technical (Advanced): " + advanced.join(", "));
        if (familiar.length > 0)
          lines.push("Technical (Familiar): " + familiar.join(", "));
        if (basic.length > 0)
          lines.push("Technical (Basic): " + basic.join(", "));
      }

      if (soft.length > 0) {
        lines.push(
          "Leadership & Soft Skills: " +
            soft
              .map(function(s) {
                return s.text || s;
              })
              .filter(Boolean)
              .join(", ")
        );
      }

      var other = this.otherSkills || [];
      if (other.length > 0) {
        lines.push(
          "Other Skills: " +
            other
              .map(function(s) {
                return s.text || s;
              })
              .filter(Boolean)
              .join(", ")
        );
      }

      lines.push("");
      return lines.join("\n");
    },
    buildCertificationsSection(subsep) {
      var certs = this.licensesCerts || [];
      var credentials = this.structuredCredentials || [];
      if (certs.length === 0 && credentials.length === 0) return null;

      var lines = [];
      lines.push("LICENSES & CERTIFICATIONS");
      lines.push(subsep);
      certs.forEach(function(c) {
        var text = c.text || c;
        if (text) lines.push("* " + text);
      });
      if (credentials.length > 0) {
        if (certs.length > 0) lines.push("");
        credentials.forEach(function(cred) {
          var entry = "* " + (cred.name || "Unknown");
          if (cred.credentialType || cred.credential_type)
            entry += " (" + (cred.credentialType || cred.credential_type) + ")";
          if (cred.issuingAuthority || cred.issuing_authority)
            entry += " — " + (cred.issuingAuthority || cred.issuing_authority);
          if (cred.jurisdiction) entry += " [" + cred.jurisdiction + "]";
          var status = cred.status || "";
          if (status) entry += " [" + status + "]";
          lines.push(entry);
        });
      }
      lines.push("");
      return lines.join("\n");
    },
    buildLanguagesSection(subsep) {
      var langs = this.languages || [];
      if (langs.length === 0) return null;

      var lines = [];
      lines.push("LANGUAGES");
      lines.push(subsep);
      langs.forEach(function(l) {
        var text = l.language || l.text || l;
        var proficiency = l.languageProficiency || l.level || "";
        if (proficiency) text = text + " (" + proficiency + ")";
        if (text) lines.push("* " + text);
      });
      lines.push("");
      return lines.join("\n");
    },
    buildProfessionalOrgsSection(subsep) {
      var profOrgs = this.professionalOrg || [];
      var hasData = profOrgs.length > 0 || !!this.professionalOrgDisplay;
      if (!hasData) return null;

      var lines = [];
      lines.push("PROFESSIONAL ORGANIZATIONS");
      lines.push(subsep);

      if (profOrgs.length > 0) {
        profOrgs.forEach(function(org) {
          var text = org.text || org;
          if (text) lines.push("* " + text);
        });
      } else if (this.professionalOrgDisplay) {
        lines.push("* " + this.professionalOrgDisplay);
      }

      lines.push("");
      return lines.join("\n");
    },
    buildVolunteerOrgsSection(subsep) {
      var volOrgs = this.volunteerOrg || [];
      var hasData = volOrgs.length > 0 || !!this.volunteerOrgDisplay;
      if (!hasData) return null;

      var lines = [];
      lines.push("VOLUNTEER & COMMUNITY LEADERSHIP");
      lines.push(subsep);

      if (volOrgs.length > 0) {
        volOrgs.forEach(function(org) {
          var text = org.text || org;
          if (text) lines.push("* " + text);
        });
      } else if (this.volunteerOrgDisplay) {
        lines.push("* " + this.volunteerOrgDisplay);
      }

      lines.push("");
      return lines.join("\n");
    },
    buildAchievementsSection(subsep) {
      var items = this.verifiedSmallWins;
      if (items.length === 0) return null;

      var lines = [];
      lines.push("VERIFIED ACHIEVEMENTS");
      lines.push(subsep);

      var self = this;
      items.forEach(function(item, index) {
        if (index > 0) {
          lines.push("");
        }

        var summary = "";
        if (item.aiDraft && item.aiDraft.summary) {
          summary = item.aiDraft.summary;
        } else if (item.rawInput) {
          summary = item.rawInput;
        } else if (item.description) {
          summary = item.description;
        }

        lines.push(index + 1 + ". " + summary);

        var draft = item.aiDraft || {};
        if (draft.situation) lines.push("   Situation: " + draft.situation);
        if (draft.task) lines.push("   Task: " + draft.task);
        if (draft.action) lines.push("   Action: " + draft.action);
        if (draft.result) lines.push("   Result: " + draft.result);

        if (item.context || item.action || item.outcome) {
          if (item.context && !draft.situation)
            lines.push("   Context: " + item.context);
          if (item.action && !draft.action)
            lines.push("   Action: " + item.action);
          if (item.outcome && !draft.result)
            lines.push("   Outcome: " + item.outcome);
        }

        if (item.rawInput && item.rawInput !== summary) {
          lines.push("   Original Note: " + item.rawInput);
        }

        if (item.linkedTo) {
          var jobLabel = self.getLinkedJobLabel(item.linkedTo);
          if (jobLabel) lines.push("   Linked to Role: " + jobLabel);
        }

        if (item.promotedTo) {
          var promotedLabel = self.getLinkedJobLabel(item.promotedTo);
          if (promotedLabel)
            lines.push("   Added to Experience: " + promotedLabel);
        }

        if (item.tags && item.tags.length > 0) {
          lines.push("   Tags: " + item.tags.join(", "));
        }

        if (item.createdAt) {
          lines.push("   Captured: " + self.formatSingleDate(item.createdAt));
        }
      });

      lines.push("");
      return lines.join("\n");
    },
    buildAllSmallWinsSection(subsep) {
      var items = this.allSmallWins;
      if (items.length === 0) return null;

      var lines = [];
      lines.push("ADDITIONAL ACHIEVEMENTS & NOTES");
      lines.push(subsep);

      var self = this;
      items.forEach(function(item) {
        lines.push("* " + (item.rawInput || item.description || ""));
        if (item.linkedTo) {
          var jobLabel = self.getLinkedJobLabel(item.linkedTo);
          if (jobLabel) lines.push("  Linked: " + jobLabel);
        }
      });

      lines.push("");
      return lines.join("\n");
    },
    buildPersonalInterestsSection(subsep) {
      // PR-PROFILE-STORY-POLISH: read the merged list (structured + legacy)
      // so the text export / PDF includes structured personal interests.
      var interests = this.personalInterestList;
      if (interests.length === 0) return null;

      var lines = [];
      lines.push("PERSONAL INTERESTS");
      lines.push(subsep);
      interests.forEach(function(interest) {
        lines.push("* " + interest);
      });
      lines.push("");
      return lines.join("\n");
    },
    buildFitPreferencesSection(subsep) {
      var answers = this.fitAnswers || [];
      if (answers.length === 0 && !this.fitAdditionalNotes) return null;

      var lines = [];
      lines.push("WORKPLACE FIT PREFERENCES");
      lines.push(subsep);
      lines.push(
        "These preferences describe the work environments, culture, and conditions where this person thrives."
      );
      lines.push("");

      var questions = this.fitQuestions || [];

      answers.forEach(function(answer, index) {
        var question = questions.find(function(q) {
          return q.id === answer.questionId;
        });
        var questionText = question
          ? question.label
          : "Preference " + answer.questionId;

        var selectedLabels = (answer.selectedOptions || [])
          .map(function(opt) {
            return opt.label || opt.text || opt;
          })
          .filter(Boolean);

        if (selectedLabels.length > 0) {
          lines.push(index + 1 + ". " + questionText);
          lines.push("   Response: " + selectedLabels.join(", "));
        }
      });

      // PR-FIT-NOTES-FIX: additional-context note appended at the end of
      // the Fit Preferences block (not in its own "Personal Statement"
      // section). Plain prefix so a downstream reader knows it's the
      // user's free-text answer, not a system caption.
      if (this.fitAdditionalNotes) {
        lines.push("");
        lines.push("Additional Context:");
        lines.push(this.fitAdditionalNotes);
      }

      lines.push("");
      return lines.join("\n");
    },
    buildCareerGoalsSection(subsep) {
      var hasIndustry = !!(
        this.targetIndustry ||
        (this.industries && this.industries.length > 0) ||
        this.industryReasons
      );
      var hasCompany = !!(
        this.targetCompany ||
        this.targetRole ||
        this.companyReasons
      );
      var hasInterests =
        this.careerInterestList.length > 0 ||
        this.personalInterestList.length > 0;

      if (!hasIndustry && !hasCompany && !hasInterests) return null;

      var lines = [];
      lines.push("CAREER GOALS & INTERESTS");
      lines.push(subsep);

      if (hasIndustry) {
        if (this.targetIndustry)
          lines.push("Target Industry: " + this.targetIndustry);
        var industryList = (this.industries || [])
          .map(function(i) {
            return i.text || i;
          })
          .filter(Boolean);
        if (industryList.length > 0)
          lines.push("Industries of Interest: " + industryList.join(", "));
        var indSkills = (this.industrySkills || [])
          .map(function(s) {
            return s.text || s;
          })
          .filter(Boolean);
        if (indSkills.length > 0)
          lines.push("Industry Skills: " + indSkills.join(", "));
        if (this.industryReasons)
          lines.push("Why This Industry: " + this.industryReasons);
      }

      if (hasCompany) {
        if (hasIndustry) lines.push("");
        if (this.targetCompany)
          lines.push("Target Company: " + this.targetCompany);
        if (this.companyDoes) lines.push("What They Do: " + this.companyDoes);
        if (this.targetRole) lines.push("Target Role: " + this.targetRole);
        if (this.companyReasons)
          lines.push("Why This Company: " + this.companyReasons);
        if (this.companyGoals) lines.push("Goals: " + this.companyGoals);
      }

      if (hasInterests) {
        if (hasIndustry || hasCompany) lines.push("");
        // PR-PROFILE-STORY-POLISH: merged lists (structured + legacy).
        var career = this.careerInterestList;
        if (career.length > 0)
          lines.push("Career Interests: " + career.join(", "));
        var personal = this.personalInterestList;
        if (personal.length > 0)
          lines.push("Personal Interests: " + personal.join(", "));
      }

      lines.push("");
      return lines.join("\n");
    },
    buildAdditionalSection(subsep) {
      var hasNotes = !!this.otherNotes;

      if (!hasNotes) return null;

      var lines = [];
      lines.push("ADDITIONAL NOTES");
      lines.push(subsep);

      lines.push(this.otherNotes);

      lines.push("");
      return lines.join("\n");
    },
    formatDateRange(start, end, isCurrent) {
      var startStr = this.formatSingleDate(start);
      var endStr = isCurrent ? "Present" : this.formatSingleDate(end);

      if (startStr && endStr) return startStr + " - " + endStr;
      if (startStr) return startStr + " - Present";
      return "";
    },
    formatSingleDate(date) {
      if (!date) return "";
      try {
        var d = new Date(date);
        if (isNaN(d.getTime())) return "";
        return d.toLocaleDateString("en-US", {
          month: "short",
          year: "numeric"
        });
      } catch (e) {
        return "";
      }
    },
    getLinkedJobLabel(linkedTo) {
      if (!linkedTo || !this.workDict || !this.workDict[linkedTo]) return null;
      var work = this.workDict[linkedTo];
      var general = work.workGeneral || {};
      var positions = (work.workRole && work.workRole.positions) || [];
      var title = positions.length > 0 ? positions[0].title : "";
      var company = general.companyName || "";
      if (title && company) return title + " at " + company;
      return company || title || linkedTo;
    },
    exportPdf: function() {
      var self = this;
      self.exportingDocx = true;
      var profileData = {
        displayValue: self.displayValue,
        firstName: self.firstName,
        lastName: self.lastName,
        contactTitle: self.contactTitle
      };
      generateCareerDossier(profileData, self.fullTextExport)
        .then(function() {
          self.exportingDocx = false;
          self.showSnackbar("Resumation Profile exported as PDF", "success");
        })
        .catch(function(err) {
          self.exportingDocx = false;
          self.showSnackbar(
            "Export failed: " + (err.message || "Unknown error"),
            "error"
          );
        });
    },
    showSnackbar: function(text, color) {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    }
  }
};
</script>

<style scoped>
.export-document-frame {
  background: #e5e7eb;
  border-radius: 4px;
  padding: 16px;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.08);
}

.export-rich-preview {
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 32px 36px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  min-height: 600px;
}

.export-title {
  font-size: 1.8rem;
  font-weight: 700;
  color: #1e3a5f;
  margin-bottom: 2px;
}

.export-header {
  border-bottom: none;
}

.export-section {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.export-section-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
}

.export-section-body {
  padding: 16px;
}

/* PR-FIT-NOTES-FIX: subtle divider between the Fit & Preferences answer
   list and the free-text Additional Context block, so the note reads as
   "extra detail this person added" rather than just another bullet. */
.fit-notes-block {
  border-top: 1px dashed rgba(0, 0, 0, 0.12);
}
.theme--dark .fit-notes-block {
  border-top-color: rgba(255, 255, 255, 0.18);
}

.export-contact-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 8px;
}

.export-contact-item {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #374151;
}

.export-work-divider {
  border-top: 1px solid #e5e7eb;
}

.export-achievement-item {
  padding-left: 12px;
  border-left: 3px solid #007aff;
}

.export-star-field {
  font-size: 13px;
  color: #4b5563;
  margin-top: 4px;
  line-height: 1.5;
}

/* SUPERPOWERS: decision-authority disclaimer under the Superpowers list.
   Quiet, italic, clearly a caption rather than a claim. */
.export-superpowers-disclaimer {
  font-size: 11px;
  font-style: italic;
  color: #6b7280;
  line-height: 1.5;
  padding: 8px 10px;
  border-radius: 6px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
}

.export-star-label {
  font-weight: 600;
  color: #374151;
}

.export-win-item {
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
  border-left: 3px solid #f59e0b;
}

.export-win-number {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #1e3a5f;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 13px;
  flex-shrink: 0;
  margin-top: 2px;
}

.opacity-80 {
  opacity: 0.8;
}

.theme--dark .export-document-frame {
  background: #1a1a2e;
  box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.3);
}

.theme--dark .export-rich-preview {
  background: #2a2a3e;
  border-color: #3a3a4e;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
}

.theme--dark .export-title {
  color: #90caf9;
}

.theme--dark .export-section {
  border-color: #3a3a4e;
}

.theme--dark .export-section-header {
  background: #232338;
  border-bottom-color: #3a3a4e;
}

.theme--dark .export-contact-item {
  color: #b0bec5;
}

.theme--dark .export-work-divider {
  border-top-color: #3a3a4e;
}

.theme--dark .export-star-field {
  color: #9e9e9e;
}

.theme--dark .export-star-label {
  color: #b0bec5;
}

.theme--dark .export-win-item {
  background: #232338;
}

.theme--dark .export-win-number {
  background: #42a5f5;
  color: #1a1a2e;
}
</style>
